import { useEffect, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, AlertCircle, Mail } from "lucide-react";
import api from "../lib/axios";

type Mode = "form" | "verify-success" | "waitlist-success";

export default function Signup() {
  const [mode, setMode] = useState<Mode>("form");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordRulesMet, setPasswordRulesMet] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });

  const navigate = useNavigate();

  const validatePasswordClientSide = (pw: string) => {
    setPasswordRulesMet({
      length: pw.length >= 8,
      lowercase: /[a-z]/.test(pw),
      uppercase: /[A-Z]/.test(pw),
      number: /\d/.test(pw),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
    });
  };

  const clearForm = () => {
    setPassword("");
    setConfirmPassword("");
  };

  const handleSignup = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || (isAdmin && !password)) {
      setError("Please fill in all required fields.");
      return;
    }

    if (isAdmin && password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (isAdmin && !Object.values(passwordRulesMet).every(Boolean)) {
      setError("Password does not meet complexity requirements.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      if (!isAdmin) {
        await api.post("/auth/waitlist", {
          email: normalizedEmail,
          source: "signup_page",
        });
        clearForm();
        setMode("waitlist-success");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      await api.post("/auth/register", {
        email: normalizedEmail,
        password,
        is_admin: true,
      });

      clearForm();
      setMode("verify-success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      console.error("❌ Signup error:", err);
      setError(err?.response?.data?.detail || "Signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "verify-success") {
      const t = setTimeout(() => {
        navigate("/verify-reminder", { state: { email } });
      }, 30000);
      return () => clearTimeout(t);
    }
  }, [mode, navigate, email]);

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-white"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="w-full max-w-4xl bg-white shadow-xl rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2">
        {/* Left Panel */}
        <div className="bg-black text-white p-10 flex flex-col justify-center">
          <h1 className="text-2xl font-bold mb-4">Create Your Infuse Ed Account</h1>
          <p className="mb-6">
            Get early access to the most powerful Canvas-integrated student success platform.
          </p>
          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <BadgeCheck className="text-green-400 w-6 h-6" />
              <span>Designed for Canvas Admins</span>
            </li>
            <li className="flex items-center gap-3">
              <BadgeCheck className="text-green-400 w-6 h-6" />
              <span>Works instantly — no sales call required</span>
            </li>
            <li className="flex items-center gap-3">
              <BadgeCheck className="text-green-400 w-6 h-6" />
              <span>Instructor version coming soon</span>
            </li>
          </ul>
        </div>

        {/* Right Panel */}
        <div className="p-10">
          {mode === "verify-success" && (
            <motion.div
              key="verify-success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-4"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <BadgeCheck className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Account created</h2>
              <p className="text-gray-600">
                We sent a verification link to <strong>{email}</strong>. Check your inbox
                (and spam), then sign in.
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Button
                  onClick={() => navigate("/verify-reminder", { state: { email } })}
                  className="bg-black text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Resend link
                </Button>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Back to Sign In
                </Button>
              </div>
            </motion.div>
          )}

          {mode === "waitlist-success" && (
            <motion.div
              key="waitlist-success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-4"
            >
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <BadgeCheck className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">You’re on the waitlist</h2>
              <p className="text-gray-600">
                We’ll email <strong>{email}</strong> as soon as the Instructor Suite is ready.
              </p>
              <div className="flex gap-3 justify-center pt-2">
                <Button variant="outline" onClick={() => navigate("/")}>
                  Back to Sign In
                </Button>
              </div>
            </motion.div>
          )}

          {mode === "form" && (
            <>
              <h2 className="text-xl font-bold mb-4">Sign Up</h2>

              {error && (
                <motion.div
                  className="mb-4 text-red-600"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              {/* ✅ Enter submits now */}
              <form onSubmit={handleSignup} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />

                {isAdmin && (
                  <>
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPassword(value);
                        validatePasswordClientSide(value);
                      }}
                      autoComplete="new-password"
                      required
                      disabled={loading}
                    />
                    <Input
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      disabled={loading}
                    />
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-600 pl-2">Passwords do not match</p>
                    )}
                    <ul className="text-xs text-gray-600 space-y-1 pl-2">
                      <li className={passwordRulesMet.length ? "text-green-600" : "text-red-600"}>
                        • At least 8 characters
                      </li>
                      <li className={passwordRulesMet.lowercase ? "text-green-600" : "text-red-600"}>
                        • One lowercase letter
                      </li>
                      <li className={passwordRulesMet.uppercase ? "text-green-600" : "text-red-600"}>
                        • One uppercase letter
                      </li>
                      <li className={passwordRulesMet.number ? "text-green-600" : "text-red-600"}>
                        • One number
                      </li>
                      <li className={passwordRulesMet.special ? "text-green-600" : "text-red-600"}>
                        • One special character (!@#$%^&* etc.)
                      </li>
                    </ul>
                  </>
                )}

                <div className="space-y-2">
                  <label className="font-medium text-sm">
                    Do you have admin access to Canvas at your school or organization?
                  </label>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant={isAdmin ? "default" : "outline"}
                      onClick={() => setIsAdmin(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      type="button"
                      variant={!isAdmin ? "default" : "outline"}
                      onClick={() => setIsAdmin(false)}
                    >
                      No
                    </Button>
                  </div>

                  {!isAdmin && (
                    <motion.div
                      className="text-sm text-gray-600 flex items-start gap-2 pt-2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <AlertCircle className="w-4 h-4 text-yellow-500 mt-1" />
                      <span>
                        Infuse Suite is currently designed for Canvas admins. Leave your email above and
                        we’ll notify you when the Instructor Suite is live.
                      </span>
                    </motion.div>
                  )}
                </div>

                <motion.div whileHover={{ scale: 1.02 }}>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-black text-white hover:bg-white hover:text-black hover:border hover:border-black transition-all duration-300"
                  >
                    {loading ? "Please wait…" : isAdmin ? "Create Account" : "Join the Waitlist"}
                  </Button>
                </motion.div>

                <p className="text-sm text-center">
                  Already have an account?{" "}
                  <Link to="/" className="underline">
                    Log In
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
